// Per-household detail page. Phase B of the Households redesign.
//
// Replaces (for the row-click flow) the previous HouseholdDetail modal.
// Each household lives at /crm/households/:id with sub-routes per tab.
// The old modal is still available via the global HouseholdDetailProvider
// for other tabs (Pipeline, etc.) — Phase E deprecates those uses.

import { useEffect, useState } from 'react';
import { Routes, Route, useParams, Navigate } from 'react-router-dom';
import { apiFetch } from '../../utils/apiFetch';
import HouseholdHeader from './HouseholdHeader';
import HouseholdTabBar from './HouseholdTabBar';
import OverviewTab from './tabs/OverviewTab';
import MembersTab from './tabs/MembersTab';
import WealthTab from './tabs/WealthTab';
import TaxTab from './tabs/TaxTab';
import InsuranceTab from './tabs/InsuranceTab';
import CrossSellTab from './tabs/CrossSellTab';
import PortalTab from './tabs/PortalTab';
import ActivityTab from './tabs/ActivityTab';

export default function HouseholdPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/data/households/${encodeURIComponent(id)}`);
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to load household (${res.status}): ${body.slice(0, 200)}`);
      }
      setData(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (loading && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-[13px] text-[#888]">Loading household...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-8 max-w-md mx-auto mt-12 text-center">
        <h2 className="text-[16px] font-bold text-rose mb-2">Could not load household</h2>
        <p className="text-[12px] text-[#666]">{error}</p>
      </div>
    );
  }
  if (!data) return null;

  // Tab content shares the data + a reload callback so child components
  // can refresh after mutations without owning their own fetch logic.
  const tabProps = {
    household: data.household,
    members: data.members,           // engagement rows (clients table)
    people: data.people || [],       // human rows (people table; Phase A.5+)
    totals: data.totals,
    recentActivity: data.recent_activity,
    reload,
  };

  return (
    <div className="space-y-4">
      <HouseholdHeader {...tabProps} />
      <HouseholdTabBar householdId={data.household.id} />
      <Routes>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview"   element={<OverviewTab   {...tabProps} />} />
        <Route path="members"    element={<MembersTab    {...tabProps} />} />
        <Route path="wealth"     element={<WealthTab     {...tabProps} />} />
        <Route path="tax"        element={<TaxTab        {...tabProps} />} />
        <Route path="insurance"  element={<InsuranceTab  {...tabProps} />} />
        <Route path="cross-sell" element={<CrossSellTab  {...tabProps} />} />
        <Route path="portal"     element={<PortalTab     {...tabProps} />} />
        <Route path="activity"   element={<ActivityTab   {...tabProps} />} />
      </Routes>
    </div>
  );
}
